import { useState } from 'react';

import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Card, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { SearchInput } from '@sage/ui-components';

import DrawerBody from './DrawerBody';
import { DrawerComponentProps } from './DrawerManager';
import DrawerTitle from './DrawerTitle';
import { useAnalytics } from 'src/AnalyticsContext';
import EnterpriseLogo from 'src/components/fragments/EnterpriseLogo';
import { StaffMember } from 'src/store/sageAdminApi';
import { pluralize } from 'src/utils/intlUtils';

export interface SelectEnterpriseDrawerProps extends DrawerComponentProps {
    currentOption?: string;
    options: StaffMember[];
}

const SearchPlaceholder = 'Search for companies';

function SelectEnterpriseDrawer(props: SelectEnterpriseDrawerProps) {
    const { onNext, currentOption, options = [] } = props;

    const { palette, spacing } = useTheme();

    const { trackSearchInput } = useAnalytics();

    const [searchValue, setSearchValue] = useState('');
    const searchInputAnalytics = trackSearchInput('Select Enterprise Drawer', SearchPlaceholder);

    const filteredOptions =
        options?.filter((option) =>
            option.enterprise?.name?.toLowerCase().includes(searchValue.toLowerCase())
        ) || [];

    const handleSelectOptions = (selectedStaffMemberProfile: StaffMember) => {
        onNext({ staffMemberProfile: selectedStaffMemberProfile });
    };

    return (
        <>
            <DrawerTitle title="Select a company" sx={{ pb: 2, backgroundColor: 'grey.50' }}>
                <SearchInput
                    searchValue={searchValue}
                    onSearchChange={setSearchValue}
                    placeholder={SearchPlaceholder}
                    {...searchInputAnalytics}
                />
            </DrawerTitle>
            <DrawerBody sx={{ gap: 2, pb: 4, backgroundColor: 'grey.50' }}>
                {filteredOptions.map((staffMemberProfile: StaffMember) => (
                    <Card
                        key={staffMemberProfile.id}
                        onClick={() => handleSelectOptions(staffMemberProfile)}
                        sx={{
                            cursor: 'pointer',
                            border:
                                currentOption && currentOption === staffMemberProfile.id
                                    ? '2px solid'
                                    : '1px solid',
                            borderColor:
                                currentOption && currentOption === staffMemberProfile.id
                                    ? 'primary.main'
                                    : 'grey.100',
                        }}
                    >
                        {currentOption && currentOption === staffMemberProfile.id && (
                            <Box
                                sx={{
                                    bgcolor: 'purple.100',
                                    py: 0.5,
                                    px: 1.5,
                                    borderRadius: spacing(1, 1, 0, 0),
                                }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    component="p"
                                    sx={{ color: 'purple.900' }}
                                >
                                    Current
                                </Typography>
                            </Box>
                        )}
                        <Stack
                            direction="row"
                            sx={{
                                alignItems: 'center',
                                gap: 1.5,
                                p: 2,
                            }}
                        >
                            <EnterpriseLogo
                                enterpriseId={staffMemberProfile?.enterprise?.id!}
                                enterpriseName={staffMemberProfile?.enterprise?.name}
                            />
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 'bold', color: 'primary' }}
                                >
                                    {staffMemberProfile?.enterprise?.name}
                                </Typography>
                                <Typography variant="subtitle1">
                                    {pluralize(
                                        staffMemberProfile.facilities?.length || 0,
                                        'community',
                                        'communities'
                                    )}
                                </Typography>
                            </Box>
                            <FontAwesomeIcon icon={faChevronRight} color={palette.common.black} />
                        </Stack>
                    </Card>
                ))}
            </DrawerBody>
        </>
    );
}

export default SelectEnterpriseDrawer;
